import type { Channel, GpsSample, Lap, Session } from '../xrk/types'

/** Lap colours: fixed categorical order, dark-surface steps, no red or green (those colour acceleration on the map). Six, so every selected lap has its own. */
export const LAP_COLORS = ['#2f6fd1', '#8e44ad', '#1f9bb5', '#c0399b', '#c8761e']
/** The grey reference plus one lap per colour. */
export const MAX_LAPS = LAP_COLORS.length + 1

export function lapColor(slot: number): string {
  return LAP_COLORS[Math.min(slot, LAP_COLORS.length - 1)]
}

/** The reference lap is white on every diagram, so it is told apart at a glance. */
export const REFERENCE_COLOR = '#b8bec9'

/** A trace's colour: grey for the reference, else its colour slot (its place among the non-reference laps). */
export function traceColor(t: { isReference?: boolean; colorSlot?: number }, slot: number): string {
  return t.isReference ? REFERENCE_COLOR : lapColor(t.colorSlot ?? slot)
}

/** A lap's samples re-expressed against distance from the lap start, in metres. */
export interface LapTrace {
  /** Unique across sessions: `${sessionId}:${lap.index}` */
  key: string
  /** Display label, e.g. "a_0389 L2" */
  label: string
  /** The lap the others are compared with (set by the analysis view) */
  isReference?: boolean
  /** Its place among the non-reference laps, which picks its colour (set by the analysis view) */
  colorSlot?: number
  lap: Lap
  /** Distance from lap start (m), strictly increasing */
  distance: Float64Array
  /** Time since lap start (s) aligned to `distance` */
  time: Float64Array
  /** Channel values aligned to `distance`, keyed by channel name */
  channels: Record<string, Float64Array>
  /** GPS points inside the lap, for the map */
  gps: GpsSample[]
}

/** Binary search: index of the first element >= x. */
export function lowerBound(arr: ArrayLike<number>, x: number): number {
  let lo = 0
  let hi = arr.length
  while (lo < hi) {
    const mid = (lo + hi) >>> 1
    if (arr[mid] < x) lo = mid + 1
    else hi = mid
  }
  return lo
}

/** Linear interpolation of a channel at time t. */
export function sampleAt(time: ArrayLike<number>, values: ArrayLike<number>, t: number): number {
  const n = time.length
  if (n === 0) return NaN
  if (t <= time[0]) return values[0]
  if (t >= time[n - 1]) return values[n - 1]
  const i = lowerBound(time, t)
  const t0 = time[i - 1]
  const t1 = time[i]
  const f = t1 === t0 ? 0 : (t - t0) / (t1 - t0)
  return values[i - 1] + f * (values[i] - values[i - 1])
}

/**
 * Cumulative distance along the session, integrated from a speed channel in m/s.
 * Returns one entry per speed sample, in metres.
 */
export function cumulativeDistance(time: ArrayLike<number>, speedMs: ArrayLike<number>): Float64Array {
  const out = new Float64Array(time.length)
  for (let i = 1; i < time.length; i++) {
    const dt = time[i] - time[i - 1]
    const v = 0.5 * (speedMs[i] + speedMs[i - 1])
    out[i] = out[i - 1] + Math.max(0, v) * dt
  }
  return out
}

/** GPS position interpolated at session time `t`, or undefined outside the recorded range. */
function gpsAt(gps: GpsSample[], t: number): GpsSample | undefined {
  if (gps.length < 2 || t < gps[0].time || t > gps[gps.length - 1].time) return undefined
  const times = gps.map((g) => g.time)
  const i = Math.max(1, lowerBound(times, t))
  const a = gps[i - 1]
  const b = gps[Math.min(i, gps.length - 1)]
  const f = b.time === a.time ? 0 : (t - a.time) / (b.time - a.time)
  return {
    time: t,
    lat: a.lat + f * (b.lat - a.lat),
    lon: a.lon + f * (b.lon - a.lon),
    alt: a.alt + f * (b.alt - a.alt),
    speed: a.speed + f * (b.speed - a.speed),
  }
}

/**
 * The lap's GPS samples with a virtual sample at exactly `lap.start` and `lap.end`, so distance
 * 0 is the start/finish crossing itself and not the first fix after it (up to one GPS period,
 * a couple of metres, later). Without this every lap starts with a small time offset and the
 * delta-time trace does not read 0 at the line.
 */
export function lapGps(session: Session, lap: Lap): GpsSample[] {
  const inside = session.gps.filter((g) => g.time > lap.start && g.time < lap.end)
  const head = gpsAt(session.gps, lap.start)
  const tail = gpsAt(session.gps, lap.end)
  return [...(head ? [head] : []), ...inside, ...(tail ? [tail] : [])]
}

/**
 * Build a distance-based trace for one lap by resampling every channel on a uniform grid.
 * `speed` must be in m/s. Step is in metres.
 */
export function buildLapTrace(
  session: Session,
  lap: Lap,
  speed: Channel,
  step = 2,
  ids: { sessionId: string; sessionName: string } = { sessionId: 'session', sessionName: '' },
): LapTrace {
  const dist = cumulativeDistance(speed.time, speed.values)
  // Distance 0 is the lap start itself (interpolated between speed samples), not the first
  // sample after it.
  const d0 = sampleAt(speed.time, dist, lap.start)
  const d1 = sampleAt(speed.time, dist, lap.end)
  const lapLength = d1 - d0
  const n = Math.max(2, Math.floor(lapLength / step) + 1)

  const distance = new Float64Array(n)
  const time = new Float64Array(n)
  const i0 = Math.max(0, lowerBound(speed.time, lap.start) - 1)
  const i1 = Math.min(lowerBound(speed.time, lap.end), speed.time.length - 1)
  // Map distance -> time by walking the integrated distance array.
  let j = i0
  for (let k = 0; k < n; k++) {
    const d = d0 + k * step
    distance[k] = k * step
    while (j < i1 && dist[j + 1] < d) j++
    const dA = dist[j]
    const dB = dist[Math.min(j + 1, i1)]
    const f = dB === dA ? 0 : Math.max(0, Math.min(1, (d - dA) / (dB - dA)))
    const t = speed.time[j] + f * (speed.time[Math.min(j + 1, i1)] - speed.time[j])
    time[k] = Math.max(0, Math.min(lap.duration, t - lap.start))
  }

  const channels: Record<string, Float64Array> = {}
  for (const ch of session.channels) {
    const vals = new Float64Array(n)
    for (let k = 0; k < n; k++) vals[k] = sampleAt(ch.time, ch.values, time[k] + lap.start)
    channels[ch.name] = vals
  }

  const gps = lapGps(session, lap)
  const label = `${ids.sessionName ? ids.sessionName + ' ' : ''}L${lap.index + 1}`
  return { key: `${ids.sessionId}:${lap.index}`, label, lap, distance, time, channels, gps }
}

/**
 * Time delta of `lap` relative to `reference` at each distance point of `lap`.
 * Positive means `lap` is slower at that point.
 */
export function deltaTime(lap: LapTrace, reference: LapTrace): Float64Array {
  const out = new Float64Array(lap.distance.length)
  for (let k = 0; k < out.length; k++) {
    out[k] = lap.time[k] - sampleAt(reference.distance, reference.time, lap.distance[k])
  }
  return out
}

export function bestLap(laps: Lap[]): Lap | undefined {
  return laps
    .filter((l) => !l.incomplete)
    .reduce<Lap | undefined>((b, l) => (!b || l.duration < b.duration ? l : b), undefined)
}

/** Find the GPS speed channel, whatever the logger called it, and return it in m/s. */
export function findSpeedChannel(session: Session): Channel | undefined {
  const ch =
    session.channels.find((c) => /^gps[ _]?speed$/i.test(c.name)) ?? session.channels.find((c) => /speed/i.test(c.name))
  if (!ch) return undefined
  const unit = ch.unit.toLowerCase()
  let factor = 1
  if (unit === 'km/h' || unit === 'kmh' || unit === 'kph') factor = 1 / 3.6
  else if (unit === 'mph') factor = 0.44704
  if (factor === 1) return ch
  const values = new Float64Array(ch.values.length)
  for (let i = 0; i < values.length; i++) values[i] = ch.values[i] * factor
  return { ...ch, unit: 'm/s', values }
}

// ---------- Track-referenced distance ----------
// Integrated distance measures how far a lap travelled, not where it is. To compare laps
// fairly, every lap is projected onto the reference lap's GPS line and placed at the arc
// length of the nearest point on that line, so equal x means the same spot on the track.

export interface ReferenceLine {
  /** Local-metre coordinates of the reference lap, [x0,y0,x1,y1,...] */
  xy: Float64Array
  /** Arc length at each point, metres from lap start */
  s: Float64Array
  /** Session time at each point */
  t: Float64Array
  /** Total length, metres */
  length: number
  lat0: number
  lon0: number
  mPerDegLat: number
  mPerDegLon: number
}

/** Metres ahead of the last match that a sample may advance in one GPS step (10 Hz => 8 m at 80 m/s). */
const SEARCH_AHEAD_M = 80
/** Metres behind the last match still considered, to absorb GPS jitter. */
const SEARCH_BACK_M = 15
/** Window around the start/finish for the first sample of a lap. */
const START_WINDOW_M = 60

export function buildReferenceLine(session: Session, lap: Lap): ReferenceLine | undefined {
  const gps = lapGps(session, lap)
  if (gps.length < 10) return undefined
  const lat0 = gps[0].lat
  const lon0 = gps[0].lon
  const mPerDegLat = 111_320
  const mPerDegLon = 111_320 * Math.cos((lat0 * Math.PI) / 180)
  const n = gps.length
  const raw = new Float64Array(n * 2)
  for (let i = 0; i < n; i++) {
    raw[2 * i] = (gps[i].lon - lon0) * mPerDegLon
    raw[2 * i + 1] = (gps[i].lat - lat0) * mPerDegLat
  }
  // Light 3-point smoothing to take the edge off GPS jitter without rounding corners.
  const xy = new Float64Array(n * 2)
  for (let i = 0; i < n; i++) {
    const a = Math.max(0, i - 1)
    const b = Math.min(n - 1, i + 1)
    xy[2 * i] = (raw[2 * a] + raw[2 * i] + raw[2 * b]) / 3
    xy[2 * i + 1] = (raw[2 * a + 1] + raw[2 * i + 1] + raw[2 * b + 1]) / 3
  }
  const s = new Float64Array(n)
  for (let i = 1; i < n; i++) s[i] = s[i - 1] + Math.hypot(xy[2 * i] - xy[2 * i - 2], xy[2 * i + 1] - xy[2 * i - 1])
  const t = Float64Array.from(gps, (g) => g.time)
  return { xy, s, t, length: s[n - 1], lat0, lon0, mPerDegLat, mPerDegLon }
}

/**
 * Arc length along the reference line of the nearest point to (x, y), searching only
 * segments whose arc length lies in [lo, hi]. Returns NaN when no segment is in range.
 */
function projectPoint(line: ReferenceLine, x: number, y: number, lo: number, hi: number): { s: number; d: number } {
  const { xy, s } = line
  const n = s.length
  let i0 = lowerBound(s, lo) - 1
  if (i0 < 0) i0 = 0
  let best = Infinity
  let bestS = NaN
  for (let i = i0; i < n - 1 && s[i] <= hi; i++) {
    const ax = xy[2 * i]
    const ay = xy[2 * i + 1]
    const bx = xy[2 * i + 2]
    const by = xy[2 * i + 3]
    const dx = bx - ax
    const dy = by - ay
    const len2 = dx * dx + dy * dy
    let f = len2 > 0 ? ((x - ax) * dx + (y - ay) * dy) / len2 : 0
    f = Math.max(0, Math.min(1, f))
    const px = ax + f * dx
    const py = ay + f * dy
    const d = Math.hypot(x - px, y - py)
    if (d < best) {
      best = d
      bestS = s[i] + f * (s[i + 1] - s[i])
    }
  }
  return { s: bestS, d: best }
}

/**
 * Build a lap trace whose distance axis is position along the reference line.
 * Falls back to the lap's own integrated distance when it has too little GPS data.
 */
export function buildProjectedTrace(
  session: Session,
  lap: Lap,
  line: ReferenceLine,
  speed: Channel,
  step = 2,
  ids: { sessionId: string; sessionName: string } = { sessionId: 'session', sessionName: '' },
): LapTrace {
  const gps = lapGps(session, lap)
  if (gps.length < 10) return buildLapTrace(session, lap, speed, step, ids)

  // Position along the reference line for each GPS sample, forced monotonic.
  const sAt: number[] = []
  const tAt: number[] = []
  let prev = -Infinity
  for (let j = 0; j < gps.length; j++) {
    const x = (gps[j].lon - line.lon0) * line.mPerDegLon
    const y = (gps[j].lat - line.lat0) * line.mPerDegLat
    let hit: { s: number; d: number }
    if (!Number.isFinite(prev)) {
      // First sample: near the start, or just before the line (end of the loop).
      const head = projectPoint(line, x, y, 0, START_WINDOW_M)
      const tail = projectPoint(line, x, y, line.length - START_WINDOW_M / 3, line.length)
      hit = tail.d < head.d ? { s: 0, d: tail.d } : head
    } else {
      hit = projectPoint(line, x, y, prev - SEARCH_BACK_M, prev + SEARCH_AHEAD_M)
    }
    if (!Number.isFinite(hit.s)) continue
    const s = Math.max(prev, hit.s)
    if (sAt.length && s <= sAt[sAt.length - 1]) continue // keep strictly increasing
    sAt.push(s)
    tAt.push(gps[j].time)
    prev = s
  }
  if (sAt.length < 5) return buildLapTrace(session, lap, speed, step, ids)

  // The lap starts and ends at the line: pin (0, start) and (length, end) so the first and
  // last grid points carry no GPS-period offset.
  if (sAt[0] > 0) {
    sAt.unshift(0)
    tAt.unshift(lap.start)
  } else {
    tAt[0] = lap.start
  }
  if (sAt[sAt.length - 1] < line.length) {
    sAt.push(line.length)
    tAt.push(lap.end)
  } else {
    tAt[tAt.length - 1] = lap.end
  }
  const sArr = Float64Array.from(sAt)
  const tArr = Float64Array.from(tAt)
  const end = line.length
  const n = Math.max(2, Math.floor(end / step) + 1)
  const distance = new Float64Array(n)
  const time = new Float64Array(n)
  for (let k = 0; k < n; k++) {
    const d = k * step
    distance[k] = d
    time[k] = Math.max(0, Math.min(lap.duration, sampleAt(sArr, tArr, d) - lap.start))
  }
  const channels: Record<string, Float64Array> = {}
  for (const ch of session.channels) {
    const vals = new Float64Array(n)
    for (let k = 0; k < n; k++) vals[k] = sampleAt(ch.time, ch.values, time[k] + lap.start)
    channels[ch.name] = vals
  }
  const label = `${ids.sessionName ? ids.sessionName + ' ' : ''}L${lap.index + 1}`
  return { key: `${ids.sessionId}:${lap.index}`, label, lap, distance, time, channels, gps }
}
